import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Observable, concat, forkJoin } from 'rxjs';
import { DestroyableComponent } from 'src/app/common/destroyable.component';
import { ConfirmationService } from 'primeng';
import { NetzTranslateService } from 'src/app/services/netz-translate.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { RegepOrderProcessService } from '../../services/regep-order-process.service';
import { RegepProcessStatus } from '../../entities/enums/regepProcessStatus';
import { RegepProcessActionTypes } from '../../entities/enums/regepProcessActionTypes';
import { RegepOrderProcess } from '../../entities/regep-order-process';
import { RegepApplicationService } from '../../services/regep-application.service';
import { RegepTechnicalResourceService } from '../regep-technical-resource/regep-technical-resource.service';
import { EegEnergyProducer } from '../../entities/regep-eeg-energy-producer';
import { concatMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ErrorService } from 'src/app/services/error/error.service';
import { UserService } from 'src/app/services/user.service';

@Component({
  selector: 'app-regep-order-process',
  templateUrl: './regep-order-process.component.html',
  styleUrls: ['./regep-order-process.component.scss'],
})
export class RegepOrderProcessComponent extends DestroyableComponent implements OnInit {
  @Input() applicationId: number;

  @Output() executedProcessAction = new EventEmitter<RegepProcessActionTypes>();

  enums = {
    orderProcessStatus: RegepProcessStatus,
  };

  orderProcess$: Observable<RegepOrderProcess>;
  role: string;
  isLoading: boolean = false;
  eegEnergyProducers: EegEnergyProducer[];

  constructor(
    private orderProcessService: RegepOrderProcessService,
    private regepService: RegepApplicationService,
    private confirmationService: ConfirmationService,
    private translateService: NetzTranslateService,
    private localStorageService: LocalStorageService,
    private regepTechnicalResourceService: RegepTechnicalResourceService,
    private router: Router,
    private errorService: ErrorService,
    private userService: UserService
  ) {
    super();
  }

  ngOnInit(): void {
    this.orderProcess$ = this.orderProcessService.orderProcess$;
    this.role = this.localStorageService.getItem('role');

    this.orderProcessService.loadOrderProcess(this.applicationId);

    this.preventLeak(this.regepTechnicalResourceService.getEegEnergyProducers()).subscribe(
      eProducers => (this.eegEnergyProducers = eProducers)
    );
  }

  get isUserEngineer(): boolean {
    return this.userService.isUserTechnicianRole();
  }

  get isUserCustomer(): boolean {
    return this.userService.isUserCustomerRole();
  }

  get isUserModerator(): boolean {
    return this.userService.isUserModeratorRole();
  }

  updateRequest(orderProcess: RegepOrderProcess): void {
    let updateRequest = this.canUpdateRegulationsRequest(orderProcess.status)
      ? this.orderProcessService.regulationsUpdateRequest(orderProcess.id)
      : this.orderProcessService.engineeringUpdateRequest(orderProcess.id);

    this.executeExternalUserAction(updateRequest);
  }

  openIssue(orderProcess: RegepOrderProcess): void {
    let openIssueRequest = this.canOpenRegulationsIssue(orderProcess.status)
      ? this.orderProcessService.regulationsOpenIssue(orderProcess.id)
      : this.orderProcessService.engineeringOpenIssue(orderProcess.id);

    this.executeInternalUserAction(openIssueRequest);
  }

  engineeringOk(orderProcess: RegepOrderProcess): void {
    let okRequest = this.isRegulationsPending(orderProcess.status)
      ? this.orderProcessService
          .regulationsOk(orderProcess.id)
          .pipe(concatMap(() => this.orderProcessService.approveRequest(orderProcess.id)))
      : this.orderProcessService
          .saveEegEnergyProducers(orderProcess.id, this.eegEnergyProducers)
          .pipe(concatMap(() => this.orderProcessService.engineeringOk(orderProcess.id)));

    this.executeInternalUserAction(okRequest);
  }

  customerDataAdded(id: number): void {
    this.executeExternalUserAction(this.orderProcessService.customerDataAdded(id));
  }

  approveRequest(id: number): void {
    this.executeInternalUserAction(this.orderProcessService.approveRequest(id));
  }

  withdrawRequest(id: number): void {
    this.confirmationService.confirm({
      message: this.translateService.translateString('regep.order-process.withdraw-request-confirmation-message'),
      accept: () => this.executeExternalUserAction(this.orderProcessService.withdrawRequest(id)),
      reject: () => this.confirmationService.close(),
    });
  }

  withdrawAndCopyRequest(id: number) {
    this.confirmationService.confirm({
      message: this.translateService.translateString('regep.order-process.withdraw-and-copy-request-confirmation-message'),
      accept: () => {
        this.isLoading = true;

        this.preventLeak(this.orderProcessService.withdrawAndCopyRequest(id, this.applicationId)).subscribe(
          result => {
            this.setRegepApplicationDataForUpdate(result);
            this.router.navigate(['/jurisdiction']);
          },
          error => this.errorService.throwErrorIfFailure(error, true)
        );
      },
      reject: () => this.confirmationService.close(),
    });
  }

  denyRequest(id: number): void {
    this.executeInternalUserAction(this.orderProcessService.denyRequest(id));
  }

  canAddCustomerData(currentStatus: RegepProcessStatus): boolean {
    return this.isUserCustomer && currentStatus === RegepProcessStatus.EngineeringOk && !this.regepService.isRegulatoryDataPending();
  }

  canWithdrawRequest(currentStatus: RegepProcessStatus): boolean {
    return (
      (this.isUserEngineer || this.regepService.isCustomerAsApplicationTechnician) &&
      [RegepProcessStatus.RequestDenied, RegepProcessStatus.RequestWithdrawn, RegepProcessStatus.RequestApproved].includes(
        currentStatus
      ) === false
    );
  }

  canDenyRequest(currentStatus: RegepProcessStatus): boolean {
    return (
      this.isUserModerator &&
      [
        RegepProcessStatus.RequestSent,
        RegepProcessStatus.EngineeringRequestUpdated,
        RegepProcessStatus.EngineeringOpenIssue,
        RegepProcessStatus.CustomerDataAdded,
        RegepProcessStatus.RegulationsRequestUpdated,
        RegepProcessStatus.RegulationsOpenIssue,
      ].includes(currentStatus)
    );
  }

  canUpdateRequest(currentStatus: RegepProcessStatus): boolean {
    return this.canUpdateEngineeringRequest(currentStatus) || this.canUpdateRegulationsRequest(currentStatus);
  }

  canOpenIssue(currentStatus: RegepProcessStatus): boolean {
    return this.canOpenEngineeringIssue(currentStatus) || this.canOpenRegulationsIssue(currentStatus);
  }

  isOkPending(currentStatus: RegepProcessStatus): boolean {
    return this.isEngineeringPending(currentStatus) || this.isRegulationsPending(currentStatus);
  }

  canApproveRequest(currentStatus: RegepProcessStatus): boolean {
    return this.isUserModerator && currentStatus === RegepProcessStatus.RegulationsOk;
  }

  getOkButtonLabel(currentStatus: RegepProcessStatus): string {
    return currentStatus < RegepProcessStatus.CustomerDataAdded
      ? 'regep.result.engineering-ok-button'
      : 'regep.result.regulations-ok-button';
  }

  private canUpdateEngineeringRequest(currentStatus: RegepProcessStatus): boolean {
    return (
      (this.isUserEngineer || this.regepService.isCustomerAsApplicationTechnician) &&
      currentStatus === RegepProcessStatus.EngineeringOpenIssue
    );
  }

  private canUpdateRegulationsRequest(currentStatus: RegepProcessStatus): boolean {
    return this.isUserCustomer && currentStatus === RegepProcessStatus.RegulationsOpenIssue;
  }

  private canOpenEngineeringIssue(currentStatus: RegepProcessStatus): boolean {
    return (
      this.isUserModerator &&
      [RegepProcessStatus.RequestSent, RegepProcessStatus.EngineeringRequestUpdated].includes(currentStatus) &&
      this.regepService.areRequiredSectionsReviewed(currentStatus)
    );
  }

  private canOpenRegulationsIssue(currentStatus: RegepProcessStatus): boolean {
    return (
      this.isUserModerator &&
      [RegepProcessStatus.CustomerDataAdded, RegepProcessStatus.RegulationsRequestUpdated].includes(currentStatus) &&
      this.regepService.areRequiredSectionsReviewed(currentStatus)
    );
  }

  private isEngineeringPending(currentStatus: RegepProcessStatus): boolean {
    return (
      this.isUserModerator &&
      [RegepProcessStatus.RequestSent, RegepProcessStatus.EngineeringRequestUpdated].includes(currentStatus) &&
      this.eegEnergyProducers?.length > 0 &&
      this.regepService.areRequiredSectionsReviewed(currentStatus)
    );
  }

  private isRegulationsPending(currentStatus: RegepProcessStatus): boolean {
    return (
      this.isUserModerator &&
      [RegepProcessStatus.CustomerDataAdded, RegepProcessStatus.RegulationsRequestUpdated].includes(currentStatus) &&
      this.regepService.areRequiredSectionsReviewed(currentStatus)
    );
  }

  private executeExternalUserAction(actionObservable: Observable<any>): void {
    this.preventLeak(actionObservable).subscribe(() => this.executedProcessAction.emit(RegepProcessActionTypes.External));
  }

  private executeInternalUserAction(actionObservable: Observable<any>): void {
    this.preventLeak(actionObservable).subscribe(() => this.executedProcessAction.emit(RegepProcessActionTypes.Internal));
  }

  private setRegepApplicationDataForUpdate(result: any) {
    this.localStorageService.setItem('update_application', 'true');
    this.localStorageService.setItem('gridcomp_update_application', 'true');
    this.localStorageService.setItem('installation_flow', 'regep');
    this.localStorageService.setItem('application_id', result.applicationId.toString());
    this.localStorageService.setItem('connection_object_id', result.connectionobject.connectionobjectId.toString());
    this.localStorageService.setItem('business_partner_id', result.businesspartner.businesspartnerId.toString());
    this.localStorageService.setItem('area_locations', result.connectionobject.areaLocations);
    this.localStorageService.setItem('location_data', {
      city: result.connectionobject.city,
      postcode: result.connectionobject.postcode,
      street: result.connectionobject.street,
      street_number: result.connectionobject.streetNo,
    });
  }
}
